import { useId, useState } from 'react';
import { isOptedOut, setOptedOut } from '../../lib/analytics';
import './AnalyticsToggle.css';

/**
 * Inline opt-out for anonymous usage analytics.
 *
 * Lives on the privacy page so a kid (or their grown-up) reading
 * about what we collect can flip the switch right there. The toggle
 * persists in localStorage and tells the PostHog client to drop any
 * queued events — see `lib/analytics.ts` for the wire-level details.
 *
 * Default: anonymous analytics ON. The component is built around
 * "opt-out, visible" rather than "opt-in, buried" because Phase 1's
 * launch hypothesis depends on a real signal of where kids drop off.
 * No personal data, no replay, no third-party trackers.
 */
export function AnalyticsToggle(): JSX.Element {
  const [optedOut, setOptedOutState] = useState(isOptedOut);
  const id = useId();
  const helpId = `${id}-help`;

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const next = !e.target.checked;
    setOptedOut(next);
    setOptedOutState(next);
  };

  return (
    <div className="bl-analytics-toggle">
      <input
        id={id}
        type="checkbox"
        checked={!optedOut}
        onChange={handleToggle}
        aria-describedby={helpId}
      />
      <label htmlFor={id} className="bl-analytics-toggle__label">
        <strong>Send anonymous usage stats</strong>
        <span className="bl-analytics-toggle__help" id={helpId}>
          Counts of trainings, exports, and where the studio gets stuck. No pictures, sounds, names,
          or anything you typed. Off any time.
        </span>
      </label>
    </div>
  );
}
